import type { ActionSummary, ContextSummary, ProjectSummary } from '../../../core/api/types';
import { actionRowFor, type ActionRow, type ContainerKind } from '../../../shared/actions/model/action-row';

/**
 * What home groups by — this page's own name for its grouping axis; the container detail pages use
 * {@link ContainerKind} directly, since they are not grouping anything.
 *
 * 030 adds `tag`, deliberately widening THIS and not
 * {@link ContainerKind}: that type says "a context or a project", the two things an action belongs
 * to, and `container.store.ts` uses it to decide which detail screen it is showing. A tag is neither,
 * so widening `ContainerKind` would let `load('tag')` typecheck against a screen that cannot exist.
 *
 * The `satisfies` buys error LOCALITY and not much else: a typo would already be rejected where
 * `GroupBy` reaches `actionRowFor`, a hundred lines away, so this puts the squiggle on the array.
 */
export const GROUP_BY_VALUES = ['context', 'project', 'tag'] as const satisfies readonly (ContainerKind | 'tag')[];

export type GroupBy = (typeof GROUP_BY_VALUES)[number];

// `ActionRow`, `SecondaryLabel` and the row builder (`actionRowFor`) moved to
// `shared/actions/model/action-row.ts` (research R-008; T022; and the 007 prerequisite chore that
// extracted `actionRowFor` out of this file's old `toRow`/`labelFor`): they are the shape — and the
// assembly — the shared action-row component needs, not a fact about grouping.

/**
 * The container a group represents, as a discriminated pair.
 *
 * `kind` and `containerId` were two independent fields, which made
 * `{ kind: 'context', containerId: null }` type-check and forced the drop path to convert and cast.
 * Pairing them states the invariant where the group is BUILT rather than where it is consumed: a
 * context or project group always has an id, and **No project** is the one kind whose container is
 * legitimately absent, because every action has a context (004) and no group stands for the absence
 * of one.
 */
export type GroupContainer =
  | { readonly kind: 'context'; readonly containerId: number }
  | { readonly kind: 'project'; readonly containerId: number }
  | { readonly kind: 'no-project'; readonly containerId: null };

/**
 * Whether this container is the one the action already occupies.
 *
 * **One statement, because two disagreeing ones fail silently.** `home.store.ts`'s `moveToContainer`
 * asks it to refuse a write that would change nothing, and `move-picker.ts`'s `destinationsFor`
 * asks it to leave the action's own group out of the picker. Those two answers have to agree or
 * the practitioner taps a destination and watches nothing happen: the write returns
 * `not-attempted`, so there is no row change, no announcement and no error — a dead entry with
 * nothing anywhere to explain it.
 *
 * They were written twice, as a `switch` here and a ternary there, with a unit test whose name
 * ("exactly as the store's own check does") existed only to hold the two copies together.
 * `/simplify` and the reuse pass both found it; a single definition gives by construction what
 * that test was asking for by convention.
 *
 * It lives beside `GroupContainer` because that is the type it destructures, and because
 * `DropDestination` IS `GroupContainer` — there is nothing to convert at either call site.
 */
export function holdsAction(container: GroupContainer, action: ActionSummary): boolean {
  return container.kind === 'context'
    ? action.contextId === container.containerId
    : (action.projectId ?? null) === container.containerId;
}

/** What every group has, whatever it groups by. */
interface GroupCommon {
  readonly key: string;
  readonly heading: string;
  readonly rows: readonly ActionRow[];
  readonly count: number;
}

/**
 * A group that IS a destination. `home-page.ts` puts it plainly: a `Group` was `kind` and
 * `containerId` as one discriminated pair, which is what let 024's drop handler and 025's picker take
 * a group and move an action into it with nothing to convert.
 */
export type ContainerGroup = GroupContainer & GroupCommon;

/**
 * A primary-tag group (030). **It carries no `containerId`, and that absence is the whole of
 * FR-008.**
 *
 * Dragging a row onto `grocery` would have to mean *retag*, which is a different write with its own
 * unspecified rules about replacing versus adding a tag — so a tag group must not be a destination.
 * Making it structurally impossible rather than refusing it at runtime is 014's shape (privilege
 * escalation is unrepresentable, not refused): `Group` is a UNION now, so passing a tag group where
 * a `GroupContainer` is expected does not compile, and the compiler found every site rather than a
 * reviewer having to.
 */
export type TagGroup = GroupCommon & { readonly kind: 'tag' };

export type Group = ContainerGroup | TagGroup;

/**
 * Narrows a group to one that can receive an action. The only way to a destination from a `Group`.
 */
export function isContainerGroup(group: Group): group is ContainerGroup {
  // Tests for the FIELD, not for a list of kinds — and neither of the two forms this replaced.
  //
  // `kind !== 'tag'` (the original) is unsound: TypeScript does not check a predicate's body, so it
  // would go on asserting `is ContainerGroup` for a fourth axis the moment one joins the union —
  // #110's manual ordering is queued — and a group with no `containerId` would reach
  // `moveToContainer`. Enumerating the three container kinds instead (the `/code-review` fix) is
  // sound but is a THIRD copy of `GroupContainer`'s member list, and a future container kind added
  // there and forgotten here would silently lose drag and the move picker for a group that has a
  // perfectly good `containerId`.
  //
  // `in` is what the union already gives us: every `ContainerGroup` arm has `containerId`, no
  // `TagGroup` does, and nothing has to be kept in step by hand.
  return 'containerId' in group;
}

export interface GroupingInput {
  readonly by: GroupBy;
  readonly contexts: readonly ContextSummary[];
  readonly projects: readonly ProjectSummary[];
  readonly actions: readonly ActionSummary[];
  /** 010 FR-006: the practitioner's calendar day, already reduced to midnight UTC by the store. */
  readonly today: Date;
  /** 010: the raw instant, for staleness — which measures elapsed time, not calendar days. */
  readonly now: Date;
  /** 009 FR-009: the practitioner's staleness-starts, read by `home.store.ts` and passed straight
   *  through to `actionRowFor`. This module reads no store and holds no default. */
  readonly stalenessStarts: number;
}

/**
 * Turns the API's flat list into the page's groups.
 *
 * **It never sorts ROWS.** The API's sequence within a group is preserved exactly (006 FR-017):
 * ordering is the server's, corrected in this feature to the reference's rule (research R-002). If a
 * client-side sort of rows ever creeps in here, `grouping.spec.ts` fails.
 *
 * 030 added one comparator and it sorts GROUP HEADINGS, not rows — the exact distinction a reader
 * auditing FR-007 needs, because the older wording ("it does not sort") is now false on its face and
 * would send that reader either to "fix" a legitimate sort or to add a second ordering elsewhere.
 * Context and project headings inherit their order from the server's contexts/projects lists; a tag
 * has no such list to inherit from, so the tag axis orders its own headings case-folded.
 */
export function groupActions({ by, contexts, projects, actions, today, now, stalenessStarts }: GroupingInput): Group[] {
  const contextNames = new Map(contexts.map((c) => [c.id, c.name]));
  const projectNames = new Map(projects.map((p) => [p.id, p.name]));
  const descriptions = new Map(actions.map((a) => [a.id, a.description]));
  // 030: a tag group is keyed by NEITHER container, and 013 already has a word for that — `'none'`,
  // which shows BOTH the context and the project name on the row "because there is no 'other'
  // container when the caller is inside no container at all". That is exactly true here, so this
  // needs no fourth value: grouping by tag reuses the search page's meaning rather than inventing one.
  const lookups = { by: by === 'tag' ? ('none' as const) : by, contextNames, projectNames, descriptions, today, now, stalenessStarts };

  const rowsFor = (predicate: (a: ActionSummary) => boolean): ActionRow[] =>
    actions.filter(predicate).map((a) => actionRowFor(a, lookups));

  if (by === 'tag') {
    // The primary tag is `tags[0]` and nothing else computes it: the API already orders an action's
    // tags case-insensitively (011 R-014, 029 R-002), so there is no second definition of
    // "alphabetically first" in this client (FR-003).
    // `?.` and not `a.tags[0]`: the comment below promises that an action whose tags are missing
    // entirely lands in the No tag group, and with a bare subscript it would instead throw and take
    // the whole `groups` computed — the entire list — down with it. `tags` is required on the wire,
    // so this is unreachable through the real API; the point is that the promise is now true.
    const primaryOf = (a: ActionSummary): string | null => a.tags?.[0] ?? null;

    // Distinct primary tags, ordered case-folded — the same rule the row's chips are ordered by, so
    // a heading's position and a chip's position cannot disagree. This orders GROUPS, never rows:
    // `rowsFor` filters and so preserves the API's sequence inside each one (FR-007).
    const names = [...new Set(actions.map(primaryOf).filter((n): n is string => n !== null))]
      // No case-sensitive tie-break: `tag_name_ci_unique` (changelog 012) makes tag names
      // case-insensitively unique per practitioner, so two DISTINCT names cannot case-fold equal
      // and the second term was unreachable. `sort` is stable, so equal keys keep arrival order —
      // the same reasoning `containers/model/reorder.ts` states for its comparator.
      .sort((l, r) => l.toLowerCase().localeCompare(r.toLowerCase()));

    const tagGroups: Group[] = names.map((name) => {
      const rows = rowsFor((a) => primaryOf(a) === name);
      return { key: `tag-${name}`, heading: name, kind: 'tag' as const, rows, count: rows.length };
    });

    // Unconditional, and last — 024's lesson about `No project`. Keyed on the ABSENCE of a primary
    // tag rather than on an empty array, so an action whose tags are missing entirely lands here too.
    const untagged = rowsFor((a) => primaryOf(a) === null);
    tagGroups.push({ key: 'no-tag', heading: 'No tag', kind: 'tag' as const, rows: untagged, count: untagged.length });
    return tagGroups;
  }

  if (by === 'context') {
    // Every context becomes a group, including empty ones (FR-002) — but not an inactive one: an
    // inactive context is not a heading on the home page (research R-002, correction to 002/004).
    // This renders a fact the API sent (`isActive`); it does not compute availability (FR-017), so
    // it filters headings only, never actions.
    return contexts.filter((context) => context.isActive).map((context) => {
      const rows = rowsFor((a) => a.contextId === context.id);
      return { key: `context-${context.id}`, heading: context.name, kind: 'context' as const, rows, count: rows.length, containerId: context.id };
    });
  }

  if (by === 'project') {
    return projectGroups(projects, rowsFor);
  }

  // Exhaustive over GROUP_BY_VALUES. Without this, the project branch was the implicit `else`, so a
  // FOURTH axis added to that array — #110's manual ordering is queued — would have silently rendered
  // PROJECT groups under a heading nobody asked for: no compile error, no failing test.
  //
  // That is the same fail-open shape this cycle fixed in `home.store.ts`'s `isGroupBy`, which used to
  // be `value === 'context' || value === 'project'`. Both read the same constant, so guarding one and
  // not the other left a reader able to check the storage vocabulary and conclude the whole axis was
  // closed. `never` closes it at the point that renders.
  const unreached: never = by;
  throw new Error(`groupActions: unhandled grouping axis ${String(unreached)}`);
}

/** The project axis: every project a heading, plus the unconditional `No project` group last. */
function projectGroups(
  projects: readonly ProjectSummary[],
  rowsFor: (predicate: (a: ActionSummary) => boolean) => ActionRow[],
): Group[] {
  const groups: Group[] = projects.map((project) => {
    const rows = rowsFor((a) => a.projectId === project.id);
    return { key: `project-${project.id}`, heading: project.name, kind: 'project' as const, rows, count: rows.length, containerId: project.id };
  });

  // 024: **No project is now unconditional**, where it used to be pushed only when it had rows.
  //
  // The conditional form made FR-006's destination vanish precisely when it was most wanted: with
  // every action projected there was nothing unprojected, so no group, so no way to un-project one
  // by dragging. It was also the only conditional group on the page — every context renders whether
  // or not it holds anything (FR-002 above), and so does every project.
  //
  // The visible cost is a "No project — No actions." heading for a practitioner who has projected
  // everything. That is the same thing an emptied context already says, and it is what makes the
  // group a stable target rather than one that appears and disappears under the pointer.
  const unprojected = rowsFor((a) => a.projectId === null || a.projectId === undefined);
  groups.push({ key: 'no-project', heading: 'No project', kind: 'no-project', rows: unprojected, count: unprojected.length, containerId: null });

  return groups;
}
